'use client';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { KnownCookie } from 'common-types';
import {
  ApiError,
  DecodedJwt,
  Session,
  createDefaultSession,
  createNewSession,
  destroySession,
  getSession,
  getSessionUsername,
  getSessionUuid,
  isLoggedIn,
} from 'fetcher-session';
import Cookies from 'js-cookie';
import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useMount } from 'react-use';
import { fetchIamUser } from '~/api/auth';
import { removeKnownCookie } from '~/hooks/cookies';
import { useCustomEvent } from '~/lib/events/customEvents';
import { fireSignedOutEvent } from '~/services/analytics/events/auth';
import {
  trackingIdentify,
  trackingIdentifyAnonymous,
  trackingUnidentify,
} from '~/services/analytics/identify';
import { IAMUser } from '~/typings/services/iam/auth';

interface SessionContextValue {
  logout: () => void;
  refetchUser: () => void;
  clientId: string;
  decodedToken?: DecodedJwt | null;
  loggedIn: boolean;
  login: (accessToken: string, refreshToken: string) => Promise<void>;
  user?: IAMUser;
}

interface SessionProviderProps {
  children: ReactNode;
  initialState?: {
    session: Session;
    iamUser?: IAMUser;
  };
}

// Create the context with undefined initial value
const SessionContext = createContext<SessionContextValue | undefined>(
  undefined,
);

// In React 19, Context is a component
export default function SessionContextProvider({
  children,
  initialState = {
    session: createDefaultSession(null),
  },
}: SessionProviderProps) {
  const queryClient = useQueryClient();
  const firedSignedOut = useRef(false);

  const [session, setSession] = useState(initialState.session);

  const loggedIn = isLoggedIn(session);

  function logout() {
    setSession(destroySession(session, null));

    removeKnownCookie(null, 'sls-seller-account-page-1-form');
    removeKnownCookie(null, 'sls_swp_po_enabled');
    removeKnownCookie(null, 'sls_swp_po');
    localStorage?.removeItem('sls-seller-account-page-1-form' as KnownCookie);

    if (!firedSignedOut.current) {
      firedSignedOut.current = true;
      fireSignedOutEvent();
      trackingUnidentify();
    }

    window.location.reload();

    queryClient.removeQueries({
      exact: false,
    });
    queryClient.clear();
    refetch();
  }

  async function login(accessToken: string, refreshToken: string) {
    firedSignedOut.current = false;

    setSession(createNewSession(null, accessToken, refreshToken));

    Cookies.set('signed_in_before' as KnownCookie, '1', {
      expires: 2000,
    });

    queryClient.removeQueries({
      exact: false,
    });
    queryClient.clear();
    refetch();
  }

  const {
    data: user,
    refetch,
    error: userFetchError,
  } = useQuery<IAMUser | null, ApiError, IAMUser>(
    ['iam-user', getSessionUuid(session) || 'anonymous'],
    () => {
      if (isLoggedIn(session)) {
        return fetchIamUser();
      }

      return null;
    },
    {
      // @ts-ignore
      placeholderData: () => ({
        ...initialState.session.decodedAccessToken,
        username: getSessionUsername(session),
      }),
      retry: 2,
      initialData: initialState.iamUser,
      staleTime: 30000,
    },
  );

  useEffect(() => {
    if (user?.roles) {
      trackingIdentify({
        email: user.email,
        username: user.username,
        external_id: user.external_id,
        phoneNumber: user.phone_number
          ? `+${user.phone_country_code}${user.phone_number}`
          : undefined,
      });
    }
  }, [user?.roles]);

  useEffect(() => {
    if (userFetchError) {
      logout();
    }
  }, [userFetchError]);

  useMount(() => {
    if (!loggedIn) {
      trackingIdentifyAnonymous();
    }
  });

  useCustomEvent('sls_logout', logout);
  useCustomEvent('sls_login', async () => {
    const newSession = await getSession();
    setSession(newSession);
    await queryClient.invalidateQueries();
  });

  const value: SessionContextValue = {
    loggedIn,
    login,
    logout,
    user,
    clientId: session.clientId,
    decodedToken: session.decodedAccessToken,
    refetchUser: refetch,
  };

  return <SessionContext value={value}>{children}</SessionContext>;
}

// Custom hook to use the session context
export function useSession() {
  const context = useContext(SessionContext);
  if (context === undefined) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
}
